import { useCallback, useEffect, useRef } from 'react';

import { bindAll } from 'bind-event-listener';

import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';

const MARKDOWN_LIST_KEYS = ['-', '*', '•'];
const MODIFIER_KEYS = ['Shift', 'Alt'];

/**
 * Handles user discard actions such as clicking outside the container
 * or pressing the Escape key.
 *
 * When `lastTriggeredFrom` is 'spaceShortcut' and the space shortcut FG is on,
 * also allows early escape via space or markdown-list keys (hyphen, asterisk, bullet)
 * to close the modal and insert the typed character into the editor.
 */
export const useUserDiscard = ({
	ref,
	onDiscard,
	lastTriggeredFrom,
	editorView,
}: {
	editorView?: EditorView;
	lastTriggeredFrom?: string;
	onDiscard: (options?: { shouldIgnoreNextSpaceTrigger?: boolean }) => void;
	ref: React.MutableRefObject<HTMLDivElement | null>;
}): void => {
	const canEarlyEscape = useRef(true);

	const handleEscapeKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (fg('platform_editor_ai_aifc_space_shortcut')) {
				if (
					canEarlyEscape.current &&
					event.key !== ' ' &&
					!MARKDOWN_LIST_KEYS.includes(event.key) &&
					!MODIFIER_KEYS.includes(event.key)
				) {
					canEarlyEscape.current = false;
				}

				if (
					!(
						event.key === 'Escape' ||
						(canEarlyEscape.current &&
							lastTriggeredFrom === 'spaceShortcut' &&
							(event.key === ' ' || MARKDOWN_LIST_KEYS.includes(event.key)))
					)
				) {
					return;
				}
			} else {
				if (event.key !== 'Escape') {
					return;
				}
			}

			event.preventDefault();
			event.stopPropagation();

		if (fg('platform_editor_ai_aifc_space_shortcut')) {
			onDiscard?.({
				shouldIgnoreNextSpaceTrigger:
					(event.key === ' ' && lastTriggeredFrom === 'spaceShortcut') ||
					(event.key === 'Escape' && lastTriggeredFrom === 'spaceShortcut'),
			});
		} else {
			onDiscard?.();
		}

		if (editorView) {
			editorView.focus();
		}

		if (
				MARKDOWN_LIST_KEYS.includes(event.key) &&
				editorView &&
				fg('platform_editor_ai_aifc_space_shortcut')
			) {
				editorView.dispatch(editorView.state.tr.insertText(` ${event.key}`));
				return;
			}
			if (
				event.key === ' ' &&
				lastTriggeredFrom === 'spaceShortcut' &&
				editorView &&
				fg('platform_editor_ai_aifc_space_shortcut')
			) {
				editorView.dispatch(editorView.state.tr.insertText(' '));
				return;
			}
		},
		[onDiscard, lastTriggeredFrom, editorView],
	);

	const handleEscapeKeyUp = useCallback((event: KeyboardEvent) => {
		if (event.key !== 'Escape') {
			return;
		}
		event.preventDefault();
		event.stopPropagation();
	}, []);

	const handleClickOutside = useCallback(
		(event: MouseEvent) => {
			if (ref.current?.contains(event.target as Node)) {
				return;
			}
			onDiscard?.();
		},
		[ref, onDiscard],
	);

	useEffect(() => {
		const contentArea = document.querySelector<HTMLElement>('.ak-editor-content-area');
		if (!contentArea) {
			return;
		}
		// Ensure we focus the content area to capture keyboard events
		contentArea.focus({ preventScroll: true });
		return bindAll(contentArea, [
			{ type: 'keydown', listener: handleEscapeKeyDown },
			{ type: 'keyup', listener: handleEscapeKeyUp },
			{ type: 'click', listener: handleClickOutside },
		]);
	}, [ref, handleEscapeKeyDown, handleEscapeKeyUp, handleClickOutside]);
};
